import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Box } from "@mui/material";
import GeneralAndContactDetails from "./GeneralAndContactDetails";
import BillingAddress from "./BillingAddress";
import DeliveryAddress from "./DeliveryAddress";

const AddressDetails = ({
  opportunity,
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const member = opportunity?.member || {};
  const rawAddress = member?.address || {};

  // Billing address is initially derived from current data or member data
  const [billingAddress, setBillingAddress] = useState(
    data.billing.street
      ? data.billing
      : {
          street: rawAddress.line1 || "",
          line: rawAddress.line2 || "-",
          city: rawAddress.city || "",
          state: rawAddress.state || "",
          zip: rawAddress.postalCode || rawAddress.zip || "",
        },
  );

  // Delivery address is derived from current data
  const [deliveryAddress, setDeliveryAddress] = useState(data.delivery);
  const [copyBilling, setCopyBilling] = useState(data.copyBilling);

  const addressesMatch = useCallback((addr1, addr2) => {
    if (!addr1 || !addr2) return false;

    const normalize = (val) => {
      if (!val) return "";
      const trimmed = val.trim().toLowerCase();
      return trimmed === "-" ? "" : trimmed;
    };

    // Only match if at least some fields are filled
    const hasContent = normalize(addr1.street) || normalize(addr1.city);
    if (!hasContent) return false;

    return (
      normalize(addr1.street) === normalize(addr2.street) &&
      normalize(addr1.line) === normalize(addr2.line) &&
      normalize(addr1.city) === normalize(addr2.city) &&
      normalize(addr1.state) === normalize(addr2.state) &&
      normalize(addr1.zip) === normalize(addr2.zip)
    );
  }, []);

  React.useEffect(() => {
    const match = addressesMatch(billingAddress, deliveryAddress);
    if (match && !copyBilling) {
      setCopyBilling(true);
      onDataChange("copyBilling", true);
    }
  }, [
    billingAddress,
    deliveryAddress,
    copyBilling,
    onDataChange,
    addressesMatch,
  ]);

  const handleBillingChange = useCallback(
    (field, value) => {
      setBillingAddress((prev) => {
        const newBilling = { ...prev, [field]: value };
        onDataChange("billing", newBilling);
        if (copyBilling) {
          const newDelivery = {
            street: newBilling.street,
            line: newBilling.line === "-" ? "" : newBilling.line,
            city: newBilling.city,
            state: newBilling.state,
            zip: newBilling.zip,
          };
          setDeliveryAddress(newDelivery);
          onDataChange("delivery", newDelivery);
        }
        return newBilling;
      });
    },
    [onDataChange, copyBilling],
  );

  const handleDeliveryChange = useCallback(
    (field, value) => {
      setDeliveryAddress((prev) => {
        const newDelivery = { ...prev, [field]: value };
        onDataChange("delivery", newDelivery);
        return newDelivery;
      });
      if (copyBilling) {
        setCopyBilling(false);
        onDataChange("copyBilling", false);
      }
    },
    [onDataChange, copyBilling],
  );

  const onGeneralDataChange = useCallback(
    (newData) => onDataChange("general", newData),
    [onDataChange],
  );

  const handleCopyToggle = useCallback(
    (checked) => {
      setCopyBilling(checked);
      onDataChange("copyBilling", checked);
      if (checked) {
        const newDelivery = {
          street: billingAddress.street,
          line: billingAddress.line === "-" ? "" : billingAddress.line,
          city: billingAddress.city,
          state: billingAddress.state,
          zip: billingAddress.zip,
        };
        setDeliveryAddress(newDelivery);
        onDataChange("delivery", newDelivery);
      } else {
        const emptyDelivery = {
          street: "",
          line: "",
          city: "",
          state: "",
          zip: "",
        };
        setDeliveryAddress(emptyDelivery);
        onDataChange("delivery", emptyDelivery);
      }
    },
    [onDataChange, billingAddress],
  );

  const [isGeneralValid, setIsGeneralValid] = useState(true);
  const [isBillingValid, setIsBillingValid] = useState(false);
  const [isDeliveryValid, setIsDeliveryValid] = useState(false);

  React.useEffect(() => {
    if (onValidationChange) {
      onValidationChange(isGeneralValid && isBillingValid && isDeliveryValid);
    }
  }, [isGeneralValid, isBillingValid, isDeliveryValid, onValidationChange]);

  React.useEffect(() => {
    if (!data.billing.street && billingAddress.street) {
      onDataChange("billing", billingAddress);
    }
  }, [data.billing.street, billingAddress, onDataChange]);

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 3, width: "100%" }}
    >
      <GeneralAndContactDetails
        opportunity={opportunity}
        data={data.general}
        onDataChange={onGeneralDataChange}
        onValidationChange={setIsGeneralValid}
        showErrors={showErrors}
      />
      <Box
        sx={{ display: "flex", gap: 3, width: "100%", alignItems: "stretch" }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <BillingAddress
            address={billingAddress}
            onChange={handleBillingChange}
            onValidationChange={setIsBillingValid}
            showErrors={showErrors}
          />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <DeliveryAddress
            address={deliveryAddress}
            onChange={handleDeliveryChange}
            copyBilling={copyBilling}
            onCopyToggle={handleCopyToggle}
            onValidationChange={setIsDeliveryValid}
            showErrors={showErrors}
          />
        </Box>
      </Box>
    </Box>
  );
};

AddressDetails.propTypes = {
  opportunity: PropTypes.object,
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default AddressDetails;
